import React, { useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Copy, PaperPlaneTilt, ArrowSquareOut, ArrowsClockwise, CheckCircle, Circle, ChatCircleDots, Eye, EyeSlash, FilePdf, Spinner, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function PortalPanel({ akteId, docs, onDocsChanged }) {
  const [st, setSt] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/akten/${akteId}/portal`, { params: { base_url: window.location.origin } }); setSt(r.data); }
    catch (err) { toast.error(errMsg(err, "Portal-Status konnte nicht geladen werden")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [akteId]);

  const link = st?.token ? `${window.location.origin}/portal/${st.token}` : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link kopiert"); }
    catch { toast.error("Kopieren nicht möglich — bitte manuell markieren"); }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/akten/${akteId}/portal/senden`, { base_url: window.location.origin });
      toast.success(`Portal-Zugang an ${r.data.to} gesendet`);
      load();
    } catch (err) { toast.error(errMsg(err, "Versand fehlgeschlagen")); }
    finally { setSending(false); }
  };

  const patch = async (payload, msg) => {
    try { const r = await api.patch(`/akten/${akteId}/portal`, { ...payload, base_url: window.location.origin }); setSt(r.data); toast.success(msg); }
    catch (err) { toast.error(errMsg(err, "Änderung fehlgeschlagen")); }
  };

  if (!st) return <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Spinner size={16} className="animate-spin" /> Lädt …</div>;

  const canSend = st.smtp_konfiguriert && !!st.mandant?.email;
  const sendHint = !st.mandant ? "Kein Mandant zugeordnet" : !st.mandant.email ? "Keine E-Mail beim Mandanten hinterlegt" : !st.smtp_konfiguriert ? "SMTP in Einstellungen → E-Mail konfigurieren" : null;

  return (
    <div className="space-y-6" data-testid="portal-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-[#D32F2F] mb-1">Mandantenportal</p>
          <h3 className="font-serif text-2xl">Zugang für {st.mandant ? `${st.mandant.vorname || ""} ${st.mandant.nachname || ""}`.trim() : "den Mandanten"}</h3>
          <p className="text-sm text-[#6B7280] mt-1">Login mit persönlichem Link + Nachname{st.mandant?.nachname ? ` („${st.mandant.nachname}“)` : ""}.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm border border-[#E5E7EB] rounded-md px-3 h-10 bg-white">
          <span className={st.aktiv ? "text-[#15803D]" : "text-[#6B7280]"}>{st.aktiv ? "Zugang aktiv" : "Zugang gesperrt"}</span>
          <Switch checked={st.aktiv} onCheckedChange={(v) => patch({ aktiv: v }, v ? "Portal-Zugang aktiviert" : "Portal-Zugang deaktiviert")} data-testid="portal-aktiv-switch" />
        </label>
      </div>

      {/* Link */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-3" data-testid="portal-link-box">
        <p className="overline text-[#9CA3AF]">Zugangslink</p>
        <div className="flex gap-2">
          <input readOnly value={link} className="flex-1 h-10 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 font-mono text-xs text-[#1A2238]" data-testid="portal-link-input" onFocus={(e) => e.target.select()} />
          <Button variant="outline" size="sm" className="h-10" onClick={copy} data-testid="portal-copy-btn"><Copy size={16} className="mr-1" /> Kopieren</Button>
          <Button variant="outline" size="sm" className="h-10" asChild><a href={link} target="_blank" rel="noreferrer" data-testid="portal-open-btn" title="Portal in neuem Tab öffnen"><ArrowSquareOut size={16} /></a></Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" className="bg-[#1A2238] hover:bg-[#2C3859]" onClick={send} disabled={!canSend || sending} title={sendHint || undefined} data-testid="portal-send-btn">
            {sending ? <Spinner size={14} className="mr-1 animate-spin" /> : <PaperPlaneTilt size={14} className="mr-1" weight="duotone" />} Per E-Mail senden
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { if (window.confirm("Neuen Link erzeugen? Der alte Link und laufende Sitzungen werden ungültig.")) patch({ neu_generieren: true }, "Neuer Zugangslink erzeugt"); }} data-testid="portal-regenerate-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Neuen Link erzeugen
          </Button>
          {sendHint && <span className="text-xs text-[#9CA3AF]" data-testid="portal-send-hint">{sendHint}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-[#4B5563] pt-2 border-t border-[#F3F4F6]">
          <p><span className="text-[#9CA3AF]">Gesendet:</span> <span data-testid="portal-gesendet">{st.gesendet_am ? `${fmt(st.gesendet_am)} an ${st.gesendet_an}` : "noch nicht"}</span></p>
          <p><span className="text-[#9CA3AF]">Letzter Login:</span> <span data-testid="portal-letzter-login">{st.letzter_login ? fmt(st.letzter_login) : "—"}</span>{st.logins ? ` (${st.logins}×)` : ""}</p>
          <p className="inline-flex items-center gap-1"><DeviceMobile size={14} /> {st.auto_link ? "Auto-Versand bei Anlage aktiv" : "Auto-Versand aus"}</p>
        </div>
      </div>

      {/* Unterschriften */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="portal-unterschriften">
        <p className="overline text-[#9CA3AF] mb-3">Digitale Unterschriften · Modus „{{ getrennt: "Vollmacht + Abtretung getrennt", kombiniert: "Kombiniertes Dokument", vollmacht: "Nur Vollmacht" }[st.unterschrift.modus]}“</p>
        <ul className="space-y-2">
          {st.unterschrift.dokumente.map((d) => (
            <li key={d.key} className="flex items-center gap-3 text-sm" data-testid={`portal-sig-${d.key}`}>
              {d.unterschrieben_am ? <CheckCircle size={20} weight="fill" className="text-[#16A34A]" /> : <Circle size={20} className="text-[#D1D5DB]" />}
              <span className="flex-1 font-medium">{d.titel}</span>
              <span className="text-xs text-[#6B7280] font-mono">{d.unterschrieben_am ? `unterschrieben ${fmt(d.unterschrieben_am)}` : "ausstehend"}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Dokument-Freigabe */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="portal-freigabe">
        <div className="flex items-baseline justify-between mb-3">
          <p className="overline text-[#9CA3AF]">Dokumente für Mandant sichtbar</p>
          <span className="text-xs text-[#6B7280]">{(docs || []).filter((d) => d.mandant_sichtbar).length} von {(docs || []).length} freigegeben</span>
        </div>
        {(docs || []).length === 0 ? (
          <p className="text-sm text-[#6B7280]">Noch keine Dokumente in dieser Akte.</p>
        ) : (
          <ul className="divide-y divide-[#F3F4F6]">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-3 py-2.5" data-testid={`portal-doc-row-${d.id}`}>
                <FilePdf size={20} weight="duotone" className={d.mandant_sichtbar ? "text-[#16A34A]" : "text-[#9CA3AF]"} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{d.original_filename}</p>
                  <p className="text-[11px] text-[#9CA3AF]">{d.kategorie}{d.source === "portal" ? " · vom Mandanten hochgeladen" : ""}</p>
                </div>
                <FreigabeSwitch doc={d} onChanged={onDocsChanged} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <Nachrichten akteId={akteId} onRead={load} />
    </div>
  );
}

export function FreigabeSwitch({ doc, onChanged, compact = false }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    setBusy(true);
    try {
      await api.post(`/dokumente/${doc.id}/freigabe`, { sichtbar: v });
      toast.success(v ? "Für Mandant freigegeben" : "Freigabe entzogen");
      onChanged?.();
    } catch (err) { toast.error(errMsg(err, "Freigabe fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  if (compact) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => toggle(!doc.mandant_sichtbar)}
        title={doc.mandant_sichtbar ? "Für Mandant sichtbar — Klick: Freigabe entziehen" : "Für Mandant freigeben"}
        className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${doc.mandant_sichtbar ? "text-[#15803D] bg-[#DCFCE7] hover:bg-[#BBF7D0]" : "text-[#9CA3AF] hover:bg-[#F3F4F6]"}`}
        data-testid={`freigabe-toggle-${doc.id}`}
      >
        {doc.mandant_sichtbar ? <Eye size={16} weight="duotone" /> : <EyeSlash size={16} />}
      </button>
    );
  }
  return (
    <label className="inline-flex items-center gap-2 text-xs text-[#4B5563]">
      {doc.mandant_sichtbar ? <Eye size={14} className="text-[#15803D]" /> : <EyeSlash size={14} className="text-[#9CA3AF]" />}
      <Switch checked={!!doc.mandant_sichtbar} disabled={busy} onCheckedChange={toggle} data-testid={`freigabe-switch-${doc.id}`} />
    </label>
  );
}

function Nachrichten({ akteId, onRead }) {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try { const r = await api.get(`/akten/${akteId}/portal/nachrichten`); setMsgs(r.data); onRead?.(); }
    catch (err) { toast.error(errMsg(err, "Nachrichten konnten nicht geladen werden")); setMsgs([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [akteId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/akten/${akteId}/portal/nachrichten`, { text: text.trim() });
      setMsgs((m) => [...(m || []), r.data]); setText("");
      toast.success("Antwort an Mandant gesendet");
    } catch (err) { toast.error(errMsg(err, "Senden fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="portal-nachrichten-panel">
      <p className="overline text-[#9CA3AF] mb-3 inline-flex items-center gap-1.5"><ChatCircleDots size={14} weight="duotone" /> Nachrichten mit dem Mandanten</p>
      <div className="max-h-72 overflow-y-auto space-y-2 mb-3" data-testid="portal-nachrichten-thread">
        {msgs === null ? <p className="text-sm text-[#6B7280]">Lädt …</p>
          : msgs.length === 0 ? <p className="text-sm text-[#6B7280]" data-testid="portal-nachrichten-leer">Noch keine Nachrichten.</p>
          : msgs.map((m) => {
            const kanzlei = m.von === "kanzlei";
            return (
              <div key={m.id} className={`flex ${kanzlei ? "justify-end" : "justify-start"}`} data-testid={`kanzlei-msg-${m.von}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${kanzlei ? "bg-[#1A2238] text-white" : "bg-[#F3F4F6] text-[#0A192F]"}`}>
                  <p className="whitespace-pre-line">{m.text}</p>
                  <p className={`text-[10px] mt-1 ${kanzlei ? "text-white/55" : "text-[#9CA3AF]"}`}>{kanzlei ? "Kanzlei" : "Mandant"} · {fmt(m.created_at)}</p>
                </div>
              </div>
            );
          })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 items-end">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Antwort an den Mandanten …" data-testid="kanzlei-msg-input" />
        <Button onClick={send} disabled={busy || !text.trim()} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="kanzlei-msg-send"><PaperPlaneTilt size={16} weight="duotone" /></Button>
      </div>
    </div>
  );
}
